/** @jsxImportSource @emotion/react */

import * as React from 'react'
import styled from '@emotion/styled'
import { css, jsx } from '@emotion/react'
import { useFocus } from '../../hooks/focus'
import { generateHash } from '../../utils/random'
import { Primary, mixins } from '@sphinx/components-styles'

export const Toggle: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({
  checked,
  disabled,
  children,
  ...props
}) => {
  const id = props.id || generateHash()
  const { focused, handleClick, handleMouseUp } = useFocus(props)

  return (
    <ToggleWithLabel htmlFor={id} onMouseUp={handleMouseUp}>
      <ToggleInput
        {...props}
        id={id}
        type="checkbox"
        checked={checked}
        focused={focused}
        disabled={disabled}
        onClick={handleClick}
      />
      <StyledToggle />
      <Label
        htmlFor={id}
        css={css`
          cursor: ${disabled ? 'initial' : 'pointer'};
          color: ${disabled ? Primary.navy[20] : Primary.navy[100]};
        `}
      >
        {children}
      </Label>
    </ToggleWithLabel>
  )
}

const Label = styled.label`
  ${mixins.menu}
  margin-left: 8px;
`

const ToggleInput = styled.input<{ focused: boolean; disabled: boolean }>`
  &:focus + div {
    ${({ focused, disabled }) =>
      focused &&
      !disabled &&
      css`
        outline-color: ${Primary.orange[100]};
        outline-offset: 2px;
        outline-style: solid;
      `};
  }
  &:checked {
    & + div {
      background: ${Primary.orange[100]};
      border: 1px solid ${Primary.orange[100]};

      &:hover {
        background: ${Primary.orange[80]};
        border: 1px solid ${Primary.orange[80]};
      }
      &::after {
        right: 3px;
        left: auto;
        top: 3px;
        background: ${Primary.neutral.white};
      }
    }

    &:disabled {
      & + div {
        &,
        &:hover {
          background: ${Primary.navy[20]};
          border: 1px solid ${Primary.navy[20]};
        }
        &::after {
          background: ${Primary.neutral.white};
        }
      }
    }
  }

  &:disabled + div {
    &,
    &:hover {
      background: ${Primary.neutral.white};
      border: 1px solid ${Primary.navy[20]};
      cursor: initial;
    }
    &::after {
      background: ${Primary.navy[20]};
    }
  }
`

const ToggleWithLabel = styled.label`
  display: flex;
  width: max-content;
  align-items: center;
  flex-flow: row nowrap;
`

const StyledToggle = styled.div`
  width: 40px;
  height: 20px;
  border-radius: 20px;
  outline: none;
  margin-left: -19px;
  position: relative;
  box-sizing: border-box;
  border: 1px solid ${Primary.navy[40]};
  background: ${Primary.neutral.white};
  cursor: pointer;

  &::after {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 50%;
    position: absolute;
    left: 3px;
    top: 3px;
    background: ${Primary.navy[100]};
  }

  &:hover {
    border: 1px solid ${Primary.navy[100]};
  }
`
